import { getOrderBookBuyerInfoAPI } from 'api/order-book';
import dayjs from 'dayjs';

/**
 * [표 설정]에 따른 컬럼 설정
 * dbCol : databaseColumns
 * col : columns
 */
export const fnSetColumn = (dbCol, col) => {
  return col?.filter((colItem) => {
    // children 속성이 있다면
    if (colItem?.children) {
      // children 배열의 각 요소에 대해 필터링 조건을 적용하여 일치하는 요소를 제거함
      colItem.children = colItem?.children?.filter((childItem) => {
        const matchingDbCol = dbCol?.find((dbItem) => dbItem?.LIST_STATUS !== 1 && dbItem?.LIST_COLUMN_NAME === childItem?.dataIndex);
        // 일치하는 항목이 없으면 true를 반환하여 해당 childItem을 유지함
        return !matchingDbCol;
      });
      // children 배열이 비어 있는 경우에는 colItem도 제거함
      return colItem?.children?.length > 0;
    } else {
      const matchingDbCol = dbCol?.find((dbItem) => dbItem?.LIST_STATUS !== 1 && dbItem?.LIST_COLUMN_NAME === colItem?.dataIndex);
      return !matchingDbCol;
    }
  });
};

/**
 * Table Cell rowSpan
 * record : 현재 셀의 모든 데이터
 * data : Table Data
 * rowIndex : 현재 셀의 index
 * colName : 비교 컬럼 이름
 * page : {page: 현재 페이지, pageSize: 페이지당 표시 개수}
 */
export const fnRowSpan = (record, data, rowIndex, colName, page) => {
  let rowSpan = 0;

  if (page && page?.page !== 1) {
    const startIndex = (page.page - 1) * page.pageSize;
    data = data.slice(startIndex, startIndex + page.pageSize);
  }

  if (rowIndex === 0 || record?.[colName] !== data?.[rowIndex - 1]?.[colName]) {
    for (let i = rowIndex; i < data?.length; i++) {
      if (data[i]?.[colName] === record?.[colName]) {
        rowSpan += 1;
      } else {
        break;
      }
    }
  }

  return { rowSpan };
};

/**
 * 오늘 날짜
 */
export const fnGetToday = (type) => {
  if (type === 'year') {
    return dayjs().format('YYYY');
  } else if (type === 'month') {
    return dayjs().format('MM');
  } else if (type === 'day') {
    return dayjs().format('DD');
  } else if (type === 'yyyymm') {
    return dayjs().format('YYYY-MM');
  } else if (type === 'yyyymmdd') {
    return dayjs().format('YYYY-MM-DD');
  } else if (type !== null) {
    return dayjs().format(type);
  } else {
    return dayjs().format('YYYY-MM-DD HH:mm:ss');
  }
};

/**
 * 날짜포맷 (string, yyyy-mm-dd)
 */
export const getFormatDate = (date) => {
  if (date) {
    const d = new Date(date);
    const yyyy = d.getFullYear();
    const mm = d.getMonth() + 1 < 10 ? '0' + (d.getMonth() + 1) : d.getMonth() + 1;
    const dd = d.getDate() < 10 ? '0' + d.getDate() : d.getDate();
    return yyyy + '-' + mm + '-' + dd;
  }
};
/**
 * 시간포맷 (string, HH:mi)
 */
export const getFormatHHmiss = (date) => {
  const d = date ? new Date(date) : new Date();
  const HH = d.getHours() < 10 ? '0' + d.getHours() : d.getHours();
  const mi = d.getMinutes() < 10 ? '0' + d.getMinutes() : d.getMinutes();
  // console.log(`${HH}:${mi}`)
  return `${HH}:${mi}:00`;
};

/**
 * 랜덤 스트링 생성(영문대문자)
 * n : 자리수
 */
export const randomAlphaCapital = (n) => {
  if (n < 2) return '';

  let value = '';
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

  for (let i = 0; i < n; i++) {
    value += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  return value;
};
/**
 * 랜덤 스트링 생성(숫자)
 * n : 자리수
 */
export const randomNumeric = (n) => {
  if (n < 2) return '';

  let value = '';
  const chars = '0123456789';

  for (let i = 0; i < n; i++) {
    value += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  return value;
};

export const sleep = (seconds) => new Promise((resolve) => setTimeout(resolve, seconds));

/**
 * 숫자 콤마 표시
 * number : 숫자
 * decimalPeriod : 소숫점 표시
 */
function formatNumberWithCommas(number, decimalPeriod) {
  const numStr = number.toString();

  const parts = numStr.split('.');

  const hasDecimal = numStr.includes('.');

  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  if (hasDecimal) {
    if (!decimalPeriod) decimalPeriod = 1;
    const decimalPart = numStr.split('.')[1].slice(0, decimalPeriod);
    return `${parts[0]}.${decimalPart}`;
  } else {
    return parts.join('.');
  }
}

/**
 * 금액 콤마 표시
 * num : 금액숫자
 */
export const getAmountWithComma = (num) => {
  let value = num;
  if (value) {
    return formatNumberWithCommas(value);
  } else {
    return 0;
  }
};

/**
 * 전화번호 표시
 * phoneNum : 전화번호
 */
export const fnCombinePhone = (phoneNum) => {
  if (phoneNum?.middle && phoneNum?.last) return `${phoneNum?.first}-${phoneNum?.middle}-${phoneNum?.last}`;
  return null;
};
export const fnSeparatePhone = (phoneNum) => {
  if (phoneNum) {
    const num = phoneNum.split('-');
    return { first: num[0], middle: num[1], last: num[2] };
  }
  return null;
};

/**
 * 이메일 표시
 * email : 이메일
 */
export const fnCombineEmail = (email) => {
  if (email?.id && email.addr) return `${email?.id}@${email?.addr}`;
  return null;
};
export const fnSeparateEmail = (email) => {
  if (email) {
    const mail = email.split('@');
    return { id: mail[0], addr: mail[1] };
  }
  return null;
};

    
/**
 * 사업자등록번호 표시
 * business_no : 사업자등록번호
 */
export const fnCombineBusinessNo = (businessNo) => {
  if (businessNo?.business_no_1 && businessNo.business_no_2 && businessNo.business_no_3) return `${businessNo.business_no_1}-${businessNo.business_no_2}-${businessNo.business_no_3}`;
  return null;
};
export const fnSeparateBusinessNo = (businessNo) => {
  if (businessNo) {
    const no = businessNo.split('-');
    return { business_no_1: no[0], business_no_2: no[1], business_no_3: no[2] };
  }
  return null;
};

/**
 * 휴대폰번호, 전화번호 유효성검사
 */
export const formatPhone = (phoneNum) => {
  //휴대폰번호
  var regCellPhone = /^(?:(010-\d{4})||(01[1|6|7|8|9]-\d{3,4}))-(\d{4})$/;
  // 일반전화

  var regPhone = /^(0(2|3[1-3]|4[1-4]|5[1-5]|6[1-4]))(\d{3,4})(\d{4})$/;

  if (regCellPhone.test(phoneNum) || regPhone.test(phoneNum)) {
    return true;
  }
  return false;
};

/**
 * 휴대폰번호 형식 자동변환
 * @param {*} num
 * @returns
 */
export const fnCellPhoneFormat = (num) => {
  const regNumData = num
    .replace(/[^0-9]/g, '') // 숫자 제외한 문자 제거
    .replace(/^(\d{2,3})(\d{3,4})(\d{4})$/, `$1-$2-$3`); // 숫자 $1(2,3)-$2(3,4)-$3(4) 자리로 치환
  return regNumData;
};

/**
 * validationCheck
 * @param {*} value
 * @param {*} checkVal
 */
export const fnVaildationCheck = (value, checkVal) => {
  let cnt = 0;
  let result = true;
  value.forEach((element, i) => {
    if (cnt > 0) {
      result = false;
      return false;
    }
    if (!element || element.length < 1) {
      alert(`${checkVal[i]}을(를) 확인해주세요.`);
      cnt++;
      result = false;
      return false;
    } else {
    }
  });

  return result;
};

/**
 * 숫자만입력
 */
export const formatNum = (num) => {
  const regNumData = num.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
  return regNumData;
};


/**
 * 숫자만입력
 *   음수 가능
 */
export const formatNum2 = (num) => {
const regNumData = num.replace(/[^-\.0-9]/g, '')
  return regNumData
};

/**
 * 대문자키 -> 소문자키
 * @param {*} obj
 * @returns
 */
export const objToLowerCase = (obj) => {
  const key = Object.keys(obj);
  const val = Object.values(obj);
  let changedObj = {};
  if(key.length ===0) {return changedObj}
  key.map((it, i) => {
    const str = it.toLowerCase();
    changedObj[str] = val[i];
  });
  return changedObj;
};
/**
 * 소문자키 -> 대문자키
 * @param {*} obj
 * @returns
 */
export const objToUpperCase = (obj) => {
  const key = Object.keys(obj);
  const val = Object.values(obj);
  let changedObj = {};
  if(key.length ===0) {return changedObj}
  key.map((it, i) => {
    const str = it.toUpperCase();
    changedObj[str] = val[i];
  });
  return changedObj;
};

/**
 *
 * 리스트 검색필터링
 * start_date 와 end_date 날짜 비교
 * @param {*} start
 * @param {*} end
 *
 */
export const compareDate = (start, end) => {
  const d1 = new Date(start);
  const d2 = new Date(end);
  // true: default // false: 시작날짜와 끝날자 설정미스
  let result = true;
  if (d1 > d2) {
    result = false;
  }

  return result;
};

/**
 * 표 설정이 존재하는 리스트의 탭별 컬럼 설정
 * @param {*} columns data.COLUMS_LIST
 * @param {*} tabSortNo tab 순서 (_1 => 1)
 */
export const fnSetTabColumn = (columns, tabSortNo) => {
  return columns
    .filter((it) => {
      const split = it.LIST_COLUMN_NAME.split('_');
      const no = split[split.length - 1];
      return no === tabSortNo;
    })
    .map((it) => ({
      ...it,
      LIST_COLUMN_NAME: it.LIST_COLUMN_NAME.replace(/_\d+$/, '')
    }));
};

/**
 * 표 설정이 존재하는 리스트의 탭별 데이터 설정
 * @param {*} data data.DATA_LIST
 * @param {*} tabSortNo tab 순서 (_1 => 1)
 */
export const fnSetTabData = (data, tabSortNo) => {
  return data.map((it) => {
    const filterData = {};
    for (const key in it) {
      const split = key.split('_');
      const no = split[split.length - 1];
      if (no === tabSortNo) {
        const originKey = key.replace(/_\d+$/, '');
        filterData[originKey] = it[key];
      }
    }
    return filterData;
  });
};

/**
 * 표설정있는 서치바 공통
 * @param {*} condition  선택한 검색조건 데이터
 * @returns
 */
export const utilSearchData = (condition) => {
  let result = {};
  let errorResult = '';
  // 어떠한 데이터든 소문자로 처리
  const lowerCondition = objToLowerCase(condition);

  // inquiry_start_date, inquiry_end_date - 문의일
  if (lowerCondition?.inquiry_start_date && !lowerCondition?.inquiry_end_date) {
    result = {};
    errorResult = '문의일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (!lowerCondition?.inquiry_start_date && lowerCondition?.inquiry_end_date) {
    result = {};
    errorResult = '문의일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (lowerCondition?.inquiry_start_date && lowerCondition?.inquiry_end_date) {
    const compareResult = compareDate(lowerCondition?.inquiry_start_date, lowerCondition?.inquiry_end_date);
    if (compareResult) {
      result.inquiry_start_date = lowerCondition?.inquiry_start_date;
      result.inquiry_end_date = lowerCondition?.inquiry_end_date;
    } else {
      result = {};
      errorResult = '문의 시작일은 종료일보다 뒤에 올수 없습니다.';
    }
  }

  // teacher_employ_start_date, teacher_employ_end_date - 채용일
  if (lowerCondition?.teacher_employ_start_date && !lowerCondition?.teacher_employ_end_date) {
    result = {};
    errorResult = '채용일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (!lowerCondition?.teacher_employ_start_date && lowerCondition?.teacher_employ_end_date) {
    result = {};
    errorResult = '채용일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (lowerCondition?.teacher_employ_start_date && lowerCondition?.teacher_employ_end_date) {
    const compareResult = compareDate(lowerCondition?.teacher_employ_start_date, lowerCondition?.teacher_employ_end_date);
    if (compareResult) {
      result.teacher_employ_start_date = lowerCondition?.teacher_employ_start_date;
      result.teacher_employ_end_date = lowerCondition?.teacher_employ_end_date;
    } else {
      result = {};
      errorResult = '시작일은 종료일보다 뒤에 올수 없습니다.';
    }
  }

  // new_curriculum_start_date, new_curriculum_end_date - 신규교육과정
  if (lowerCondition?.new_curriculum_start_date && !lowerCondition?.new_curriculum_end_date) {
    result = {};
    errorResult = '신규교육과정 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (!lowerCondition?.new_curriculum_start_date && lowerCondition?.new_curriculum_end_date) {
    result = {};
    errorResult = '신규교육과정 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (lowerCondition?.new_curriculum_start_date && lowerCondition?.new_curriculum_end_date) {
    const compareResult = compareDate(lowerCondition?.new_curriculum_start_date, lowerCondition?.new_curriculum_end_date);
    if (compareResult) {
      result.new_curriculum_start_date = lowerCondition?.new_curriculum_start_date;
      result.new_curriculum_end_date = lowerCondition?.new_curriculum_end_date;
    } else {
      result = {};
      errorResult = '신규교육과정 시작일은 종료일보다 뒤에 올수 없습니다.';
    }
  }
  
  // approval_start_date, approval_end_date - 처리결과
  if (lowerCondition?.approval_start_date && !lowerCondition?.approval_end_date) {
    result = {};
    errorResult = '처리결과 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (!lowerCondition?.approval_start_date && lowerCondition?.approval_end_date) {
    result = {};
    errorResult = '처리결과 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (lowerCondition?.approval_start_date && lowerCondition?.approval_end_date) {
    const compareResult = compareDate(lowerCondition?.approval_start_date, lowerCondition?.approval_end_date);
    if (compareResult) {
      result.approval_start_date = lowerCondition?.approval_start_date;
      result.approval_end_date = lowerCondition?.approval_end_date;
    } else {
      result = {};
      errorResult = '처리결과 시작일은 종료일보다 뒤에 올수 없습니다.';
    }
  }

  // require_curriculum_start_date, require_curriculum_end_date - 필수교육과정
  if (lowerCondition?.require_curriculum_start_date && !lowerCondition?.require_curriculum_end_date) {
    result = {};
    errorResult = '필수교육과정 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (!lowerCondition?.require_curriculum_start_date && lowerCondition?.require_curriculum_end_date) {
    result = {};
    errorResult = '필수교육과정 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (lowerCondition?.require_curriculum_start_date && lowerCondition?.require_curriculum_end_date) {
    const compareResult = compareDate(lowerCondition?.require_curriculum_start_date, lowerCondition?.require_curriculum_end_date);
    if (compareResult) {
      result.require_curriculum_start_date = lowerCondition?.require_curriculum_start_date;
      result.require_curriculum_end_date = lowerCondition?.require_curriculum_end_date;
    } else {
      result = {};
      errorResult = '필수교육과정 시작일은 종료일보다 뒤에 올수 없습니다.';
    }
  }

  // open_start_date, open_end_date - 오픈일
  if (lowerCondition?.open_start_date && !lowerCondition?.open_end_date) {
    result = {};
    errorResult = '오픈일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (!lowerCondition?.open_start_date && lowerCondition?.open_end_date) {
    result = {};
    errorResult = '오픈일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (lowerCondition?.open_start_date && lowerCondition?.open_end_date) {
    const compareResult = compareDate(lowerCondition?.open_start_date, lowerCondition?.open_end_date);
    if (compareResult) {
      result.open_start_date = lowerCondition?.open_start_date;
      result.open_end_date = lowerCondition?.open_end_date;
    } else {
      result = {};
      errorResult = '오픈일 시작일은 종료일보다 뒤에 올수 없습니다.';
    }
  }

  // order_change_request_start_date, order_change_request_end_date - 주문변경요청일
  if (lowerCondition?.order_change_request_start_date && !lowerCondition?.order_change_request_end_date) {
    result = {};
    errorResult = '주문변경요청일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (!lowerCondition?.order_change_request_start_date && lowerCondition?.order_change_request_end_date) {
    result = {};
    errorResult = '주문변경요청일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (lowerCondition?.order_change_request_start_date && lowerCondition?.order_change_request_end_date) {
    const compareResult = compareDate(lowerCondition?.order_change_request_start_date, lowerCondition?.order_change_request_end_date);
    if (compareResult) {
      result.order_change_request_start_date = lowerCondition?.order_change_request_start_date;
      result.order_change_request_end_date = lowerCondition?.order_change_request_end_date;
    } else {
      result = {};
      errorResult = '주문변경요청일 시작일은 종료일보다 뒤에 올수 없습니다.';
    }
  }

  // order_start_date, order_end_date - 주문일
  if (lowerCondition?.order_start_date && !lowerCondition?.order_end_date) {
    result = {};
    errorResult = '주문일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (!lowerCondition?.order_start_date && lowerCondition?.order_end_date) {
    result = {};
    errorResult = '주문일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (lowerCondition?.order_start_date && lowerCondition?.order_end_date) {
    const compareResult = compareDate(lowerCondition?.order_start_date, lowerCondition?.order_end_date);
    if (compareResult) {
      result.order_start_date = lowerCondition?.order_start_date;
      result.order_end_date = lowerCondition?.order_end_date;
    } else {
      result = {};
      errorResult = '주문일 시작일은 종료일보다 뒤에 올수 없습니다.';
    }
  }

  // last_order_start_date, last_order_end_date - 최종주문일
  if (lowerCondition?.last_order_start_date && !lowerCondition?.last_order_end_date) {
    result = {};
    errorResult = '최종주문일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (!lowerCondition?.last_order_start_date && lowerCondition?.last_order_end_date) {
    result = {};
    errorResult = '최종주문일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (lowerCondition?.last_order_start_date && lowerCondition?.last_order_end_date) {
    const compareResult = compareDate(lowerCondition?.last_order_start_date, lowerCondition?.last_order_end_date);
    if (compareResult) {
      result.last_order_start_date = lowerCondition?.last_order_start_date;
      result.last_order_end_date = lowerCondition?.last_order_end_date;
    } else {
      result = {};
      errorResult = '최종주문일 시작일은 종료일보다 뒤에 올수 없습니다.';
    }
  }

  // join_start_date, join_end_date - 최종주문일
  if (lowerCondition?.join_start_date && !lowerCondition?.join_end_date) {
    result = {};
    errorResult = '회원가입일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (!lowerCondition?.join_start_date && lowerCondition?.join_end_date) {
    result = {};
    errorResult = '회원가입일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (lowerCondition?.join_start_date && lowerCondition?.join_end_date) {
    const compareResult = compareDate(lowerCondition?.join_start_date, lowerCondition?.join_end_date);
    if (compareResult) {
      result.join_start_date = lowerCondition?.join_start_date;
      result.join_end_date = lowerCondition?.join_end_date;
    } else {
      result = {};
      errorResult = '회원가입일 시작일은 종료일보다 뒤에 올수 없습니다.';
    }
  }

  // propose_start_date, propose_end_date - 인계검색일
  if (lowerCondition?.propose_start_date && !lowerCondition?.propose_end_date) {
    result = {};
    errorResult = '인계검색일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (!lowerCondition?.propose_start_date && lowerCondition?.propose_end_date) {
    result = {};
    errorResult = '인계검색일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (lowerCondition?.propose_start_date && lowerCondition?.propose_end_date) {
    const compareResult = compareDate(lowerCondition?.propose_start_date, lowerCondition?.propose_end_date);
    if (compareResult) {
      result.propose_start_date = lowerCondition?.propose_start_date;
      result.propose_end_date = lowerCondition?.propose_end_date;
    } else {
      result = {};
      errorResult = '인계검색일 시작일은 종료일보다 뒤에 올수 없습니다.';
    }
  }

  // process_start_date, process_end_date - 처리일
  if (lowerCondition?.process_start_date && !lowerCondition?.process_end_date) {
    result = {};
    errorResult = '처리일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (!lowerCondition?.process_start_date && lowerCondition?.process_end_date) {
    result = {};
    errorResult = '처리일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (lowerCondition?.process_start_date && lowerCondition?.process_end_date) {
    const compareResult = compareDate(lowerCondition?.process_start_date, lowerCondition?.process_end_date);
    if (compareResult) {
      result.process_start_date = lowerCondition?.process_start_date;
      result.process_end_date = lowerCondition?.process_end_date;
    } else {
      result = {};
      errorResult = '처리일 시작일은 종료일보다 뒤에 올수 없습니다.';
    }
  }

  // regress_propose_start_date, regress_propose_end_date - 처리일
  if (lowerCondition?.regress_propose_start_date && !lowerCondition?.regress_propose_end_date) {
    result = {};
    errorResult = '처리일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (!lowerCondition?.regress_propose_start_date && lowerCondition?.regress_propose_end_date) {
    result = {};
    errorResult = '처리일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (lowerCondition?.regress_propose_start_date && lowerCondition?.regress_propose_end_date) {
    const compareResult = compareDate(lowerCondition?.regress_propose_start_date, lowerCondition?.regress_propose_end_date);
    if (compareResult) {
      result.regress_propose_start_date = lowerCondition?.regress_propose_start_date;
      result.regress_propose_end_date = lowerCondition?.regress_propose_end_date;
    } else {
      result = {};
      errorResult = '처리일 시작일은 종료일보다 뒤에 올수 없습니다.';
    }
  }

  // search_start_date, search_end_date - 검색일
  if (lowerCondition?.search_start_date && !lowerCondition?.search_end_date) {
    result = {};
    errorResult = '검색일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (!lowerCondition?.search_start_date && lowerCondition?.search_end_date) {
    result = {};
    errorResult = '검색일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (lowerCondition?.search_start_date && lowerCondition?.search_end_date) {
    const compareResult = compareDate(lowerCondition?.search_start_date, lowerCondition?.search_end_date);
    if (compareResult) {
      result.search_start_date = lowerCondition?.search_start_date;
      result.search_end_date = lowerCondition?.search_end_date;
    } else {
      result = {};
      errorResult = '검색일 시작일은 종료일보다 뒤에 올수 없습니다.';
    }
  }

  // create_start_date, create_end_date - 생성일
  if (lowerCondition?.create_start_date && !lowerCondition?.create_end_date) {
    result = {};
    errorResult = '검색일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (!lowerCondition?.create_start_date && lowerCondition?.create_end_date) {
    result = {};
    errorResult = '검색일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (lowerCondition?.create_start_date && lowerCondition?.create_end_date) {
    const compareResult = compareDate(lowerCondition?.create_start_date, lowerCondition?.create_end_date);
    if (compareResult) {
      result.create_start_date = lowerCondition?.create_start_date;
      result.create_end_date = lowerCondition?.create_end_date;
    } else {
      result = {};
      errorResult = '검색일 시작일은 종료일보다 뒤에 올수 없습니다.';
    }
  }

  // refund_start_date, refund_end_date - 환불접수일
  if (lowerCondition?.refund_start_date && !lowerCondition?.refund_end_date) {
    result = {};
    errorResult = '환불접수일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (!lowerCondition?.refund_start_date && lowerCondition?.refund_end_date) {
    result = {};
    errorResult = '환불접수일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (lowerCondition?.refund_start_date && lowerCondition?.refund_end_date) {
    const compareResult = compareDate(lowerCondition?.refund_start_date, lowerCondition?.refund_end_date);
    if (compareResult) {
      result.refund_start_date = lowerCondition?.refund_start_date;
      result.refund_end_date = lowerCondition?.refund_end_date;
    } else {
      result = {};
      errorResult = '환불접수 시작일은 종료일보다 뒤에 올수 없습니다.';
    }
  }

  // newsletter_start_date, newsletter_end_date - 소식지 주문기간 일
  if (lowerCondition?.newsletter_start_date && !lowerCondition?.newsletter_end_date) {
    result = {};
    errorResult = '소직지 주문일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (!lowerCondition?.newsletter_start_date && lowerCondition?.newsletter_end_date) {
    result = {};
    errorResult = '소직지 주문일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (lowerCondition?.newsletter_start_date && lowerCondition?.newsletter_end_date) {
    const compareResult = compareDate(lowerCondition?.newsletter_start_date, lowerCondition?.newsletter_end_date);
    if (compareResult) {
      result.newsletter_start_date = lowerCondition?.newsletter_start_date;
      result.newsletter_end_date = lowerCondition?.newsletter_end_date;
    } else {
      result = {};
      errorResult = '소직지 주문일 시작일은 종료일보다 뒤에 올수 없습니다.';
    }
  }

  // counsel_start_date, counsel_end_date - 상담일
  if (lowerCondition?.counsel_start_date && !lowerCondition?.counsel_end_date) {
    result = {};
    errorResult = '상담일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (!lowerCondition?.counsel_start_date && lowerCondition?.counsel_end_date) {
    result = {};
    errorResult = '상담일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (lowerCondition?.counsel_start_date && lowerCondition?.counsel_end_date) {
    const compareResult = compareDate(lowerCondition?.counsel_start_date, lowerCondition?.counsel_end_date);
    if (compareResult) {
      result.counsel_start_date = lowerCondition?.counsel_start_date;
      result.counsel_end_date = lowerCondition?.counsel_end_date;
    } else {
      result = {};
      errorResult = '상담일 시작일은 종료일보다 뒤에 올수 없습니다.';
    }
  }

  // memo_start_date, memo_end_date - 메모일
  if (lowerCondition?.memo_start_date && !lowerCondition?.memo_end_date) {
    result = {};
    errorResult = '메모일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (!lowerCondition?.memo_start_date && lowerCondition?.memo_end_date) {
    result = {};
    errorResult = '메모일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (lowerCondition?.memo_start_date && lowerCondition?.memo_end_date) {
    const compareResult = compareDate(lowerCondition?.memo_start_date, lowerCondition?.memo_end_date);
    if (compareResult) {
      result.memo_start_date = lowerCondition?.memo_start_date;
      result.memo_end_date = lowerCondition?.memo_end_date;
    } else {
      result = {};
      errorResult = '메모일 시작일은 종료일보다 뒤에 올수 없습니다.';
    }
  }

  // approve_start_date, approve_end_date - 승인일
  if (lowerCondition?.approve_start_date && !lowerCondition?.approve_end_date) {
    result = {};
    errorResult = '메모일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (!lowerCondition?.approve_start_date && lowerCondition?.approve_end_date) {
    result = {};
    errorResult = '메모일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (lowerCondition?.approve_start_date && lowerCondition?.approve_end_date) {
    const compareResult = compareDate(lowerCondition?.approve_start_date, lowerCondition?.approve_end_date);
    if (compareResult) {
      result.approve_start_date = lowerCondition?.approve_start_date;
      result.approve_end_date = lowerCondition?.approve_end_date;
    } else {
      result = {};
      errorResult = '메모일 시작일은 종료일보다 뒤에 올수 없습니다.';
    }
  }

  // year_month(year, month) - 전송일
  if (lowerCondition?.year_month?.year && !lowerCondition?.year_month?.month) {
    result = {};
    errorResult = '전송일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (!lowerCondition?.year_month?.year && lowerCondition?.year_month?.month) {
    result = {};
    errorResult = '처리일 시작날짜와 종료날짜를 둘다 선택해주세요.';
  }
  if (lowerCondition?.year_month?.year && lowerCondition?.year_month?.month) {
    result.send_date = lowerCondition?.year_month?.year + '-' + lowerCondition?.year_month?.month;
  }

  if (lowerCondition?.base_start_date && lowerCondition?.base_end_date) {
    const compareResult = compareDate(lowerCondition?.base_start_date, lowerCondition?.base_end_date);
    if (compareResult) {
      result.base_start_date = lowerCondition?.base_start_date;
      result.base_end_date = lowerCondition?.base_end_date;
    } else {
      result = {};
      errorResult = '시작일은 종료일보다 뒤에 올수 없습니다.';
    }
  }
  // base_date - 기준일
  if (lowerCondition?.base_date) {
    if (lowerCondition?.base_date === '') {
      result = {};
      errorResult = '기준일을 선택해주세요.';
    } else {
      result.base_date = lowerCondition?.base_date;
    }
  }
  if (lowerCondition?.base_year_month) {
    if (lowerCondition?.base_year_month === '') {
      result = {};
      errorResult = '기준일을 선택해주세요.';
    } else {
      result.base_year_month = lowerCondition?.base_year_month;
    }
  }

  // 날짜설정에서 생긴 result 타입검증을 위해
  if (typeof result !== 'string') {
    // branch_id - 지사
    if (lowerCondition?.branch_id) {
      result.branch_id = lowerCondition?.branch_id;
    }

    // counsel_1st_status, counsel_2st_status - 상담상태 1차 2차
    if (lowerCondition?.counsel_1st_status) {
      if (lowerCondition?.counsel_1st_status !== '-1') {
        result.counsel_1st_status = lowerCondition?.counsel_1st_status;
      }
    }
    if (lowerCondition?.counsel_2st_status) {
      if (lowerCondition?.counsel_2st_status !== -1) {
        result.counsel_2st_status = lowerCondition?.counsel_2st_status;
      }
    }

    // sido, sigungu_cd - 개설희망지역
    if (lowerCondition?.sido && !lowerCondition?.sigungu_cd) {
      result = {};
      if(lowerCondition?.sido === '-1'){
        delete result.sido;
        delete result.sigungu_cd;
        errorResult = '개설희망지역은 둘다 선택해주세요.';
      }
    }
    if (lowerCondition?.sido && lowerCondition?.sigungu_cd) {
      if(lowerCondition?.sido === '-1' && lowerCondition?.sigungu_cd === '-1'){
        delete result.sido;
        delete result.sigungu_cd;
      }else if(lowerCondition?.sido !== '-1' && lowerCondition?.sigungu_cd !== '-1'){
        delete result.sido;
        result.sigungu_cd = lowerCondition?.sigungu_cd;
      }else{
        errorResult = '개설희망지역은 둘다 선택해주세요.';
      }
    }

    // success_rate - 가맹성공율
    if (lowerCondition?.success_rate) {
      if (lowerCondition?.success_rate !== '-1') {
        result.success_rate = lowerCondition?.success_rate;
      }
    }

    // approval_status - 처리결과
    if (lowerCondition?.approval_status) {
      if (lowerCondition?.approval_status !== '-1') {
        result.approval_status = lowerCondition?.approval_status;
      }
    }

    // regress_status - 승인상태
    if (lowerCondition?.regress_status) {
      if (lowerCondition?.regress_status !== '-1') {
        result.regress_status = lowerCondition?.regress_status;
      }
    }

    // admin_status_detail - 사유
    if (lowerCondition?.admin_status_detail) {
      if (lowerCondition?.admin_status_detail !== '-1') {
        result.admin_status_detail = lowerCondition?.admin_status_detail;
      }
    }

    // search_state, search_content - 검색조건
    if (lowerCondition?.search_content) {
        if (lowerCondition?.search_state == '-1') {
          result = {};
          errorResult = '검색조건을 선택해주세요.';
        }else{
          result.search_state = lowerCondition?.search_state;
          result.search_content = encodeURIComponent(lowerCondition?.search_content);
        }
    }

    // cognitive_path - 인지경로
    if (lowerCondition?.cognitive_path?.length > 0) {
      const str = lowerCondition?.cognitive_path.join(',');
      result.cognitive_path = str;
    }

    // progress_status - 진행상태
    if (lowerCondition?.progress_status) {
      if (lowerCondition?.progress_status !== '-1') {
        result.progress_status = lowerCondition?.progress_status;
      }
    }

    // observe_status - 참관상태
    if (lowerCondition?.observe_status) {
      if (lowerCondition?.observe_status !== '-1') {
        result.observe_status = lowerCondition?.observe_status;
      }
    }

    // school_status - 교육원상태
    if (lowerCondition?.school_status) {
      if (lowerCondition?.school_status !== '-1') {
        result.school_status = lowerCondition?.school_status;
      }
    }
    

    // close_date - 마감일
    if (lowerCondition?.close_date) {
      result.close_date = lowerCondition?.close_date;
    }

    // submit_status - 제출현황
    if (lowerCondition?.submit_status) {
      if (lowerCondition?.submit_status !== '-1') {
        result.submit_status = lowerCondition?.submit_status;
      }
    }

    // branch_idx - 지사idx
    if (lowerCondition?.branch_idx) {
      result.branch_idx = lowerCondition?.branch_idx;
    }
    // branch_name - 지사명
    if (lowerCondition?.branch_name) {
      result.branch_name = lowerCondition?.branch_name;
    }

    // school_name - 교육원명
    if (lowerCondition?.school_name) {
      result.school_name = encodeURIComponent(lowerCondition?.school_name);
    }

    // new_test_score, new_test_score_state - 신규평가점수
    if (lowerCondition?.new_test_score) {
      result.new_test_score = lowerCondition?.new_test_score;
      result.new_test_score_state = lowerCondition?.new_test_score_state;
    }

    // regular_test_score, regular_test_score_state - 신규평가점수
    if (lowerCondition?.regular_test_score) {
      result.regular_test_score = lowerCondition?.regular_test_score;
      result.regular_test_score_state = lowerCondition?.regular_test_score_state;
    }

    // grade - 등급제
    if (lowerCondition?.grade) {
      if (lowerCondition?.grade !== '-1') {
        result.grade = lowerCondition?.grade;
      }
    }

    // order_limit - 주문제한
    if (lowerCondition?.order_limit) {
      if (lowerCondition?.order_limit !== '-1') {
        result.order_limit = lowerCondition?.order_limit;
      }
    }

    // observe_school - 참관지도교육원
    if (lowerCondition?.observe_school) {
      if (lowerCondition?.observe_school !== '-1') {
        result.observe_school = lowerCondition?.observe_school;
      }
    }

    // business_license - 사업자등록증
    if (lowerCondition?.business_license) {
      if (lowerCondition?.business_license !== '-1') {
        result.business_license = lowerCondition?.business_license;
      }
    }

    // member_type - 회원구분
    if (lowerCondition?.member_type) {
      if (lowerCondition?.member_type !== '-1') {
        result.member_type = lowerCondition?.member_type;
      }
    }

    // age - 나이
    if (lowerCondition?.age) {
      if (lowerCondition?.age !== -1) {
        result.age = lowerCondition?.age;
      }
    }

    // curriculum - 교육과정
    if (lowerCondition?.curriculum) {
      if (lowerCondition?.curriculum !== '-1') {
        result.curriculum = lowerCondition?.curriculum;
      }
    }

    // exception_approve - 예외교습승인
    if (lowerCondition?.exception_approve) {
      if (lowerCondition?.exception_approve !== '-1') {
        result.exception_approve = lowerCondition?.exception_approve;
      }
    }

    // marketing_agree - 마켓팅동의
    if (lowerCondition?.marketing_agree === 1 || lowerCondition?.marketing_agree === 0) {
      if (lowerCondition?.marketing_agree !== -1) {
        result.marketing_agree = lowerCondition?.marketing_agree;
      }
    }

    // approval_status - 승인상태
    if (lowerCondition?.approval_status) {
      if (lowerCondition?.approval_status !== '-1') {
        result.approval_status = lowerCondition?.approval_status;
      }
    }

    // observe_status - 강사승인상태
    if (lowerCondition?.academy_regist_yn >= 0) {
      if (lowerCondition?.academy_regist_yn !== '-1') {
        result.academy_regist_yn = lowerCondition?.academy_regist_yn;
      }
    }

    // branch_idx - 지사
    if (lowerCondition?.branch_idx) {
      result.branch_idx = lowerCondition?.branch_idx;
    }

    // school_idx - 교육원
    if (lowerCondition?.school_idx) {
      if (lowerCondition?.school_idx !== '') {
        result.school_idx = lowerCondition?.school_idx;
      }
    }

    // process_status - 처리상태
    if (lowerCondition?.process_status) {
      if (lowerCondition?.process_status !== '-1') {
        result.process_status = lowerCondition?.process_status;
      }
    }

    // period_flag, start_school_start_period, start_school_end_period - 재원시간
    if (lowerCondition?.period_flag !== '-1') {
      if (lowerCondition?.start_school_start_period) {
        result.start_school_start_period = lowerCondition?.start_school_start_period;
      }

      if (lowerCondition?.start_school_end_period) {
        result.start_school_end_period = lowerCondition?.start_school_end_period;
      }

      if (lowerCondition?.start_school_end_period === '6' || lowerCondition?.start_school_end_period === '12') {
        delete result.start_school_start_period;
      }
    }

    // memo - 메모
    if (lowerCondition?.memo) {
      if (lowerCondition?.memo !== '') {
        result.memo = lowerCondition?.memo;
      }
    }
    // exception_reason - 예외교습사유
    if (lowerCondition?.exception_reason) {
      if (lowerCondition?.exception_reason !== '') {
        result.exception_reason = lowerCondition?.exception_reason;
      }
    }

    // send_status - 전송상태
    if (lowerCondition?.send_status) {
      if (lowerCondition?.send_status !== -1) {
        result.send_status = lowerCondition?.send_status;
      }
    }

    // subject_name - 과목선택
    if (lowerCondition?.product_class_idx) {
      if (lowerCondition?.product_class_idx !== '-1') {
        result.product_class_idx = lowerCondition?.product_class_idx;
      }
    }

    // category - 물품 카테고리
    if (lowerCondition?.product_cate_cd) {
      if (lowerCondition?.product_cate_cd !== '-1') {
        result.product_cate_cd = lowerCondition?.product_cate_cd;
      }
    }

    // category - 카테고리
    if (lowerCondition?.category) {
      if (lowerCondition?.category !== '-1') {
        result.category = lowerCondition?.category;
      }
    }
    // order_status - 주문상태
    if (lowerCondition?.order_status) {
      if (lowerCondition?.order_status !== '-1') {
        result.order_status = lowerCondition?.order_status;
      }
    }

    // refund_status - 환불상태
    if (lowerCondition?.refund_status) {
      if (lowerCondition?.refund_status !== '-1') {
        result.refund_status = lowerCondition?.refund_status;
      }
    }

    // request_reason - 요청사유 (주문변경)
    if (lowerCondition?.request_reason) {
      if (lowerCondition?.request_reason !== -1) {
        result.request_reason = lowerCondition?.request_reason;
      }
    }

    // modify_status - 처리상태 (주문변경)
    if (lowerCondition?.modify_status) {
      if (lowerCondition?.modify_status !== -1) {
        result.modify_status = lowerCondition?.modify_status;
      }
    }

    // member_name - 회원명
    if (lowerCondition?.member_name) {
      if (lowerCondition?.member_name !== '') {
        result.member_name = lowerCondition?.member_name;
      }
    }

    // product_name - 교재명
    if (lowerCondition?.product_name) {
      if (lowerCondition?.product_name !== '') {
        result.product_name = lowerCondition?.product_name;
      }
    }

    // refund_exception_yn - 환불요청예외승인여부
    if (lowerCondition?.refund_exception_yn) {
      if (lowerCondition?.refund_exception_yn !== '-1') {
        result.refund_exception_yn = lowerCondition?.refund_exception_yn;
      }
    }

    // branch_list - 지사
    if (lowerCondition?.branch_list?.length > 0) {
      result.branch_list = lowerCondition?.branch_list;
    }

    // counsel_content - 상담내용
    if (lowerCondition?.counsel_content) {
      if (lowerCondition?.counsel_content !== '') {
        result.counsel_content = lowerCondition?.counsel_content;
      }
    }

    // excellent_school_year - 우수교육원 이였던 년도
    if (lowerCondition?.excellent_school_year) {
      if (lowerCondition?.excellent_school_year !== '') {
        result.excellent_school_year = lowerCondition?.excellent_school_year;
      }
    }

    // senior_year - 수석 년도
    if (lowerCondition?.senior_year) {
      if (lowerCondition?.senior_year !== '') {
        result.senior_year = lowerCondition?.senior_year;
      }
    }

    // junior_year - 선임 년도
    if (lowerCondition?.junior_year) {
      if (lowerCondition?.junior_year !== '') {
        result.junior_year = lowerCondition?.junior_year;
      }
    }

    // yyyymm - 검색기간 년-월
    if (lowerCondition?.yyyymm) {
      if (lowerCondition?.yyyymm !== '') {
        result.yyyymm = lowerCondition?.yyyymm;
      }
    }

    // counsel_status - 상담상태
    if (lowerCondition?.counsel_status) {
      if (lowerCondition?.counsel_status !== '-1') {
        result.counsel_status = lowerCondition?.counsel_status;
      }
    }

    // search_year - 검색년도
    if (lowerCondition?.search_year) {
      if (lowerCondition?.search_year !== '') {
        result.search_year = lowerCondition?.search_year;
      }
    }

    // study_date - 학습기간
    if (lowerCondition?.study_date) {
      if (lowerCondition?.study_date !== '') {
        result.study_date = lowerCondition?.study_date;
      }
    }

    // process_type - 처리사유
    if (lowerCondition?.process_type) {
      if (lowerCondition?.process_type !== '-1') {
        result.process_type = lowerCondition?.process_type;
      }
    }

    // collect_content_status - 회수내역상태
    if (lowerCondition?.collect_content_status?.length > 0) {
      const newObject = {}
      lowerCondition.collect_content_status?.map(it => newObject[it] = 1)
      result = {...result, ...newObject}
    }

    // collect_status - 회수진행상태
    if (lowerCondition?.collect_status) {
      if (lowerCondition?.collect_status !== -1) {
        result.collect_status = lowerCondition?.collect_status;
      }
    }

    // delivery_charge_yn - 배송비결제여부
    if (lowerCondition?.delivery_charge_yn >= 0) {
      result.delivery_charge_yn = lowerCondition?.delivery_charge_yn;
    }

    // collect_request_type - 회수요청구분
    if (lowerCondition?.collect_request_type) {
      if (lowerCondition?.collect_request_type !== -1) {
        result.collect_request_type = lowerCondition?.collect_request_type;
      }
    }

    //start_school_start_period - 재원기간 시작
    if (lowerCondition?.start_school_start_period) {
      if (lowerCondition?.start_school_start_period !== 0) {
        result.start_school_start_period = lowerCondition?.start_school_start_period;
      }
    }

    //start_school_end_period - 재원기간 종료
    if (lowerCondition?.start_school_start_period) {
      if (lowerCondition?.start_school_end_period !== 0) {
        result.start_school_end_period = lowerCondition?.start_school_end_period;
      }
    }

    // teacher_grade - 강사직급
    if (lowerCondition?.teacher_grade) {
      if (lowerCondition?.teacher_grade !== '-1') {
        result.teacher_grade = lowerCondition?.teacher_grade;
      }
    }

    //study_date - 학습기간 검색
    if (lowerCondition?.study_date) {
      if (lowerCondition?.study_date !== '') {
        result.study_date = lowerCondition?.study_date;
      }
    }

    // new_order_yn - 신규교재주문 확인
    if (lowerCondition?.new_order_yn) {
      if (lowerCondition?.new_order_yn !== '-1') {
        result.new_order_yn = lowerCondition?.new_order_yn;
      }
    }

    // handover_branch_idx - 인계지사명
    if (lowerCondition?.handover_branch_idx) {
      if (lowerCondition?.handover_branch_idx !== '-1') {
        result.handover_branch_idx = lowerCondition?.handover_branch_idx;
      }
    }

    // takeover_branch_idx - 인수지사명
    if (lowerCondition?.takeover_branch_idx) {
      if (lowerCondition?.takeover_branch_idx !== '-1') {
        result.takeover_branch_idx = lowerCondition?.takeover_branch_idx;
      }
    }

    // point_history - 적립금내용
    if (lowerCondition?.point_history) {
      if (lowerCondition?.point_history !== '') {
        result.point_history = lowerCondition?.point_history;
      }
    }
  }

  // errorResult Type - String  result Type - object
  return errorResult !== '' ? errorResult : result;
};

/**
 * NHN KCP 결제 모듈
 * @param {*} param
 * @returns
 */
export const fnPaymentModule = async (param, fnCallback) => {
  const { schoolIdx, merchantUid, name, amount, taxable, tmpOrderNo='', newOrderYn=0, usePoint, deliveryPrice, additionalPrice=0, orderModifyIdx=0, yyyymm='', deliveryIdx=0 } = param;
  const siteCd = `${taxable ? process.env.REACT_APP_PAMENT_SITE_CODE_TAX : process.env.REACT_APP_PAMENT_SITE_CODE_NON_TAX}`;
  let body = {}

  // 전역 함수 선언(NHN KCP에서 호출 필요)
  window.m_Completepayment = (FormOrJson, closeEvent) => {
    try {
      const frm = document.order_info;
      // eslint-disable-next-line
      GetField( frm, FormOrJson );
  
      if (frm.res_cd.value === "0000") {
        // alert("결제 승인 요청 전,\n\n반드시 결제창에서 고객님이 결제 인증 완료 후\n\n리턴 받은 ordr_chk 와 업체 측 주문정보를\n\n다시 한번 검증 후 결제 승인 요청하시기 바랍니다."); //업체 연동 시 필수 확인 사항.
        /* 가맹점 리턴값 처리 영역 */
        fnCallback(frm);
      } else {
        alert( "[" + frm.res_cd.value + "] " + frm.res_msg.value );
      }
    } catch (err) {
      alert('결제 요청중 오류가 발생했습니다.')
      console.error(err)
    } finally {
      document.body.removeChild(form);
      closeEvent();
    }
  }

  // 페이지 벗어날 때 전역 함수 삭제
  window.addEventListener("beforeunload", () => {
    delete window.m_Completepayment;
  });

  try {
    const { data } = await getOrderBookBuyerInfoAPI(schoolIdx)
    const { data_info } = data
    body = {
      buyr_name: data_info?.BUYER_NAME,
      buyr_mail: data_info?.BUYER_EMAIL,
      buyr_tel2: data_info?.BUYER_TEL,
      buyr_postcode: data_info?.BUYER_POSTCODE,
      buyr_addr: data_info?.BUYER_ADDR
    }
  } catch (err) {
    console.error(err)
  }

  // 동적으로 폼 생성
  const form = document.createElement("form");
  form.name = "order_info";
  form.method = "post";
  form.action = `${process.env.REACT_APP_API_BASEURL}/orderBook/rt_url`; // 승인요청 URL

  body = {
    ...body,
    site_cd: siteCd, // 사이트 코드
    site_name: "큐미르",
    pay_method: "100000000000", // 카드 결제
    ordr_idxx: merchantUid,
    good_name: name,
    good_mny: amount,
    currency: "WON", // 원화(KRW)
    res_cd: '',
    res_msg: '',
    enc_info: '',
    enc_data: '',
    tran_cd: '',
    ordr_chk: '',
    tmp_order_no: tmpOrderNo,
    new_order_yn: newOrderYn,
    school_idx: schoolIdx,
    payment_method: 'card', // 내부 결제 구분(card,point,admin)
    use_point: usePoint,
    delivery_price: deliveryPrice,
    additional_price: additionalPrice,
    order_modify_idx: orderModifyIdx,
    delivery_idx: deliveryIdx,
    yyyymm
  };

  // 폼에 hidden input 추가
  Object.keys(body).forEach((key) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = key;
    input.value = body[key];
    form.appendChild(input);
  });
  document.body.appendChild(form);

  if (amount === 0) {
    // 전액 포인트 결제
    form.payment_method.value = 'point'
    fnCallback(form)
  } else {
    if (window.KCP_Pay_Execute_Web) {
      try {
        // 결제 창을 띄우는 함수
        await window.KCP_Pay_Execute_Web(form);
      } catch (err) {
        /* IE 에서 결제 정상종료시 throw로 스크립트 종료 */
        console.error("결제 창 실행 오류:", err);
        alert("결제 창을 실행할 수 없습니다. 다시 시도해주세요.");
      }
    } else {
      alert("결제 시스템이 정상적으로 로드되지 않았습니다. 새로고침 후 다시 시도해주세요.");
    }
  }
};